import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, ShieldCheck, MessageCircle, Star, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, timeAgo, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RateDialog, useRatingState } from "@/components/RateDialog";
import { OfferBubble } from "@/components/OfferBubble";
import { PickupBubble, PickupForm } from "@/components/Pickup";
import { BuyerReliability } from "@/components/TrustBadges";

const QUICK = {
  seller: ["Yes, still available!", "Can you pick up today?", "Sorry, it's sold."],
  buyer: ["Is it still available?", "When can I pick it up?", "Can you send more photos?"],
};

function Thread({ conv, onSent }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [rateOpen, setRateOpen] = useState(false);
  const [rating, reloadRating] = useRatingState(conv.listing_id, conv.role === "buyer" ? user : null);
  const end = useRef();
  const [planning, setPlanning] = useState(false);
  const refresh = () => { api.get(`/conversations/${conv.id}/messages`).then((r) => setMsgs(r.data)); onSent(); };
  const agreed = msgs.some((m) => m.kind === "offer" && m.offer_status === "accepted");
  const confirmedPickup = [...msgs].reverse().find((m) => m.kind === "pickup" && m.pickup_status === "confirmed");

  useEffect(() => {
    let prev = -1;
    const load = () => {
      if (document.hidden) return;
      api.get(`/conversations/${conv.id}/messages`).then((r) => {
        setMsgs(r.data);
        if (r.data.length !== prev) window.dispatchEvent(new Event("isoldit:unread"));
        prev = r.data.length;
      });
    };
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [conv.id]);
  useEffect(() => { const el = end.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/conversations/${conv.id}/messages`, { text });
      setMsgs((m) => [...m, data]); setText(""); onSent();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center border-b border-white/10">
        <Link to={`/listing/${conv.listing_id}`} data-testid="thread-listing-link" className="flex min-w-0 flex-1 items-center gap-3 p-4 hover:bg-white/5">
          {conv.listing_image && <img src={imgUrl(conv.listing_image)} alt="" className="h-12 w-12 rounded-lg object-cover" />}
          <div className="min-w-0"><p className="truncate font-bold">{conv.listing_title}</p><p className="text-xs text-slate-400">with {conv.role === "buyer" ? conv.seller_name : conv.buyer_name}</p>{conv.role === "seller" && <div className="mt-1"><BuyerReliability stats={conv.buyer_stats} testId="thread-buyer-reliability" /></div>}</div>
        </Link>
        {rating.eligible && (
          <button data-testid="thread-rate-seller-btn" onClick={() => setRateOpen(true)} className="mr-4 flex shrink-0 items-center gap-1 rounded-full border border-[#d4f253]/40 px-3 py-1.5 text-xs font-semibold text-[#d4f253] hover:bg-[#d4f253]/10">
            <Star className="h-3.5 w-3.5" />{rating.rating ? `${rating.rating.stars}★` : "Rate seller"}
          </button>
        )}
        <RateDialog open={rateOpen} onOpenChange={setRateOpen} listingId={conv.listing_id} sellerName={conv.seller_name} existing={rating.rating} onDone={reloadRating} />
      </div>
      <div className="flex gap-2 bg-[#d4f253]/10 px-4 py-2 text-xs text-[#d4f253]"><ShieldCheck className="h-4 w-4 shrink-0" />Pay only at pick-up after inspecting. Never send money upfront.</div>
      <div ref={end} data-testid="thread-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
        {msgs.map((m) => {
          const me = m.sender_id === user.id;
          if (m.kind === "pickup") return <PickupBubble key={m.id} m={m} me={me} onChange={refresh} onReschedule={() => setPlanning(true)} />;
          if (m.kind === "offer") return <OfferBubble key={m.id} m={m} me={me} fromSeller={m.sender_id === conv.seller_id} onChange={() => { api.get(`/conversations/${conv.id}/messages`).then((r) => setMsgs(r.data)); onSent(); }} />;
          return (
            <div key={m.id} className={`flex ${me ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${me ? "rounded-br-sm bg-[#d4f253] text-[#0f2a33]" : "rounded-bl-sm bg-white/10"}`}>
                <p className="whitespace-pre-line">{m.text}</p>
                <p className={`mt-1 text-[10px] ${me ? "text-[#0f2a33]/60" : "text-slate-400"}`}>{timeAgo(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
      {planning && <PickupForm convId={conv.id} onDone={() => { setPlanning(false); refresh(); }} onCancel={() => setPlanning(false)} />}
      {agreed && !planning && !confirmedPickup && (
        <button data-testid="thread-plan-pickup-btn" onClick={() => setPlanning(true)} className="mx-4 mb-1 flex items-center justify-center gap-2 rounded-xl border border-sky-400/50 py-2 text-xs font-bold text-sky-300 hover:bg-sky-400/10"><CalendarClock className="h-4 w-4" />Price agreed — plan the pick-up day &amp; time</button>
      )}
      {confirmedPickup && !planning && (
        <p data-testid="thread-pickup-confirmed" className="mx-4 mb-1 flex items-center justify-center gap-2 rounded-xl bg-[#d4f253]/15 py-2 text-xs font-bold text-[#d4f253]"><CalendarClock className="h-4 w-4" />Pick-up: {new Date(confirmedPickup.pickup_at).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}{confirmedPickup.place ? ` · ${confirmedPickup.place}` : ""}
          <button data-testid="thread-pickup-change-btn" onClick={() => setPlanning(true)} className="ml-1 underline">change</button></p>
      )}
      <div className={`flex gap-2 overflow-x-auto px-4 pt-2 ${planning ? "hidden" : ""}`}>
        {QUICK[conv.role === "seller" ? "seller" : "buyer"].map((q, i) => <button key={q} data-testid={`thread-quick-reply-${i}`} onClick={() => setText(q)} className="shrink-0 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/15">{q}</button>)}
      </div>
      <form onSubmit={send} className="flex gap-2 p-4">
        <input data-testid="thread-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="field flex-1 text-sm" />
        <button data-testid="thread-send-btn" className="btn-lime px-5"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

export default function Messages() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [convs, setConvs] = useState(null);
  const load = () => api.get("/conversations").then((r) => setConvs(r.data));
  useEffect(() => { load(); }, [cid]);
  const current = convs?.find((c) => c.id === cid);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <h1 className="mb-6 font-display text-3xl font-extrabold tracking-tight">Messages</h1>
      <div className="grid h-[70vh] overflow-hidden rounded-3xl bg-[#173742] md:grid-cols-[320px_1fr]">
        <div className={`overflow-y-auto border-r border-white/10 ${cid ? "hidden md:block" : ""}`}>
          {convs?.length === 0 && <div data-testid="messages-empty" className="p-8 text-center text-sm text-slate-400"><MessageCircle className="mx-auto mb-3 h-8 w-8" />No conversations yet. Message a seller from any listing.</div>}
          {convs?.map((c) => (
            <button key={c.id} data-testid={`conversation-item-${c.id}`} onClick={() => nav(`/messages/${c.id}`)} className={`flex w-full items-center gap-3 border-b border-white/5 p-4 text-left transition-colors hover:bg-white/5 ${c.id === cid ? "bg-white/10" : ""}`}>
              {c.listing_image ? <img src={imgUrl(c.listing_image)} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" /> : <div className="h-12 w-12 shrink-0 rounded-lg bg-white/10" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2"><p className="truncate text-sm font-bold">{c.role === "buyer" ? c.seller_name : c.buyer_name}</p><span className="shrink-0 text-[10px] text-slate-500">{timeAgo(c.updated_at)}</span></div>
                <p className="truncate text-xs text-slate-400">{c.listing_title}</p>
                <p className={`truncate text-xs ${c.unread ? "font-bold text-[#d4f253]" : "text-slate-500"}`}>{c.last_message}</p>
              </div>
            </button>
          ))}
        </div>
        <div className={`${cid ? "" : "hidden md:block"} min-h-0`}>
          {current ? (
            <div className="flex h-full flex-col">
              <button data-testid="thread-back-btn" onClick={() => nav("/messages")} className="flex items-center gap-1 px-4 pt-3 text-xs text-slate-400 md:hidden"><ArrowLeft className="h-4 w-4" />All chats</button>
              <div className="min-h-0 flex-1"><Thread conv={current} onSent={load} /></div>
            </div>
          ) : (
            <div className="grid h-full place-items-center text-sm text-slate-500">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
